import { Check, Minus, X } from 'lucide-react';
import {
  formatAssetLegLabel,
  humanizePredictionLabel,
} from '@/utils/marketPredictionCopy';
import type { MarketPredictionScorecard, MarketPredictionScorecardRow } from '@/services/api';

function formatPct(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function pctClass(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return 'text-gray-500';
  if (value > 0) return 'text-emerald-700';
  if (value < 0) return 'text-rose-700';
  return 'text-gray-600';
}

function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
}

function ResultIcon({ ok }: { ok: boolean | null | undefined }) {
  if (ok == null) return <Minus className="h-4 w-4 text-gray-300" aria-hidden />;
  if (ok) return <Check className="h-4 w-4 text-emerald-600" aria-hidden />;
  return <X className="h-4 w-4 text-rose-600" aria-hidden />;
}

type ScorecardTableProps = {
  title: string;
  rows: MarketPredictionScorecardRow[];
};

function ScorecardTable({ title, rows }: ScorecardTableProps) {
  const tableRows = rows ?? [];
  if (!tableRows.length) return null;

  return (
    <div>
      <p className="text-sm font-medium text-gray-500 mb-3">{title}</p>
      <div className="overflow-x-auto rounded-2xl border border-gray-100">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-3 font-medium">Symbol</th>
              <th className="px-4 py-3 font-medium">Predicted</th>
              <th className="px-4 py-3 font-medium">Actual</th>
              <th className="px-4 py-3 font-medium">Error</th>
              <th className="px-4 py-3 font-medium">Range</th>
              <th className="px-4 py-3 font-medium text-center">Dir</th>
              <th className="px-4 py-3 font-medium text-center">Range</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tableRows.map((row) => (
              <tr key={row.symbol} className="bg-white">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{formatAssetLegLabel(row.symbol)}</p>
                  {row.predicted_direction ? (
                    <p className="text-xs capitalize text-gray-400">{row.predicted_direction} call</p>
                  ) : null}
                </td>
                <td className={`px-4 py-3 font-medium ${pctClass(row.predicted_change_pct)}`}>
                  {formatPct(row.predicted_change_pct)}
                </td>
                <td className={`px-4 py-3 font-medium ${pctClass(row.actual_change_pct)}`}>
                  {formatPct(row.actual_change_pct)}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {row.error_pct != null ? formatPct(row.error_pct).replace('+', '') : '—'}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {row.predicted_range_pct
                    ? `${formatPct(row.predicted_range_pct[0])} to ${formatPct(row.predicted_range_pct[1])}`
                    : '—'}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex justify-center" title={row.direction_correct ? 'Direction correct' : 'Direction miss'}>
                    <ResultIcon ok={row.direction_correct} />
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex justify-center" title={row.range_correct ? 'Within predicted range' : 'Outside predicted range'}>
                    <ResultIcon ok={row.range_correct} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type MarketPredictionScorecardProps = {
  scorecard: MarketPredictionScorecard;
};

export function MarketPredictionScorecardPanel({ scorecard }: MarketPredictionScorecardProps) {
  const summary = scorecard.summary;
  const indexRows = (scorecard.rows ?? []).filter((row) => row.group === 'index');
  const sectorRows = (scorecard.rows ?? []).filter((row) => row.group === 'sector');
  const hasRows = indexRows.length > 0 || sectorRows.length > 0;

  if (!summary && !hasRows) return null;

  return (
    <section className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm">
      <div className="mb-8">
        <p className="text-sm font-medium text-gray-500 mb-2">Prediction scorecard</p>
        <h4 className="text-2xl text-gray-900 tracking-tight">
          {formatDateLabel(scorecard.target_date)} results
        </h4>
        <p className="text-sm text-gray-500 mt-1">
          Forecast from {formatDateLabel(scorecard.reference_date)} vs what the session delivered
        </p>
      </div>

      {summary ? (
        <div className="grid gap-4 sm:grid-cols-3 mb-8">
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
            <p className="text-sm text-gray-500 mb-1">Direction</p>
            <p className="text-3xl font-medium text-gray-900">{summary.combined_direction ?? '—'}</p>
            <p className="text-xs text-gray-400 mt-1">Predicted up/down/flat matched actual</p>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
            <p className="text-sm text-gray-500 mb-1">Range</p>
            <p className="text-3xl font-medium text-gray-900">{summary.combined_range ?? '—'}</p>
            <p className="text-xs text-gray-400 mt-1">Actual move landed inside forecast band</p>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
            <p className="text-sm text-gray-500 mb-1">Avg error</p>
            <p className="text-3xl font-medium text-gray-900">
              {summary.combined_mae != null ? `${summary.combined_mae.toFixed(2)}%` : '—'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Mean absolute pct miss across legs</p>
          </div>
        </div>
      ) : null}

      {scorecard.market ? (
        <div className="mb-8 rounded-2xl border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 mb-3">Market call</p>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div>
              <p className="text-gray-400 text-xs mb-1">Predicted SPY</p>
              <p className={`font-medium ${pctClass(scorecard.market.predicted_spy_change_pct)}`}>
                {formatPct(scorecard.market.predicted_spy_change_pct)}
                {scorecard.market.predicted_overall_direction ? (
                  <span className="ml-2 text-xs capitalize text-gray-500">
                    ({humanizePredictionLabel(scorecard.market.predicted_overall_direction)})
                  </span>
                ) : null}
              </p>
            </div>
            <div className="text-gray-300">→</div>
            <div>
              <p className="text-gray-400 text-xs mb-1">Actual SPY</p>
              <p className={`font-medium ${pctClass(scorecard.market.actual_spy_change_pct)}`}>
                {formatPct(scorecard.market.actual_spy_change_pct)}
                {scorecard.market.actual_spy_direction ? (
                  <span className="ml-2 text-xs capitalize text-gray-500">
                    ({humanizePredictionLabel(scorecard.market.actual_spy_direction)})
                  </span>
                ) : null}
              </p>
            </div>
            <div className="ml-auto">
              <ResultIcon ok={scorecard.market.direction_correct} />
            </div>
          </div>
        </div>
      ) : null}

      <div className="space-y-8">
        <ScorecardTable title="Index forecasts" rows={indexRows} />
        <ScorecardTable title="Sector forecasts" rows={sectorRows} />
      </div>
    </section>
  );
}
